// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xGoogleAnalyticsUpdate } from "@ces/App/Handlers/GoogleAnalytics/xGoogleAnalytics";
import xngGaCustomVirtualPageViewEvent from "@ces/App/Handlers/GoogleAnalytics/XngGaCustomVirtualPageViewEvent";
import { CustomAppState } from "@ces/App/Store/Reducers";
import { XNGAGE_CUSTOM_WIDGET } from "@ces/XDL/constants";
import { color } from "@ces/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    const currentPage = getCurrentPage(state).type;

    return {
        currentPage,
    };
};

const mapDispatchToProps = {
    xGoogleAnalyticsUpdate,
    xngGaCustomVirtualPageViewEvent,
};

const enum fields {
    includeCustomVirtualPageViewEventName = "includeCustomVirtualPageViewEventName",
    includeCustomVirtualPageViewEvent = "includeCustomVirtualPageViewEvent",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.includeCustomVirtualPageViewEventName]: string;
        [fields.includeCustomVirtualPageViewEvent]: boolean;
    };
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps> & HasShellContext;

export interface GoogleAnalyticsWidgetStyles {
    wrapper?: InjectableCss;
    instructionText?: TypographyPresentationProps;
}

export const googleAnalyticsWidgetStyles: GoogleAnalyticsWidgetStyles = {
    wrapper: {
        css: css`
            padding: 10px;
            background-color: ${color("secondary.main")};
        `,
    },
    instructionText: {
        color: "primary.contrast",
        variant: "h6",
        css: css`
            margin: 0;
        `,
    },
};

const styles = googleAnalyticsWidgetStyles;

const GoogleAnalyticsWidget: FC<Props> = ({
    fields,
    currentPage,
    shellContext,
    xGoogleAnalyticsUpdate,
    xngGaCustomVirtualPageViewEvent,
}) => {
    if (shellContext.isInShell && shellContext.isEditing) {
        // Placeholder in CMS view
        return (
            <StyledWrapper {...styles.wrapper}>
                <Typography {...styles.instructionText}>{translate("Google Analytics Widget (GA4)")}</Typography>
            </StyledWrapper>
        );
    }

    useEffect(() => {
        xGoogleAnalyticsUpdate({
            customVirtualPageViewEventName: fields.includeCustomVirtualPageViewEventName,
            includeCustomVirtualPageViewEvent: fields.includeCustomVirtualPageViewEvent || false,
        });
    }, [fields.includeCustomVirtualPageViewEventName, fields.includeCustomVirtualPageViewEvent]);

    useEffect(() => {
        xngGaCustomVirtualPageViewEvent({});
    }, [currentPage]);

    return null;
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(GoogleAnalyticsWidget)),
    definition: {
        displayName: "Google Analytics Management (GA4)",
        group: XNGAGE_CUSTOM_WIDGET,
        fieldDefinitions: [
            {
                name: fields.includeCustomVirtualPageViewEventName,
                displayName: "Custom Virtual Page View Event Name",
                editorTemplate: "TextField",
                defaultValue: "custom_virtualPageView",
                fieldType: "General",
            },
            {
                name: fields.includeCustomVirtualPageViewEvent,
                displayName: "Include Custom Virtual Page View Event (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
